import React, { useEffect, useState } from 'react';
import { Table, Button, Input, Dropdown, Menu, Tag, Select } from 'antd';
import { EllipsisOutlined, SearchOutlined, UserOutlined, StopOutlined, CheckCircleOutlined, DownloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { CSVLink } from 'react-csv';
import PageHeadingBanner from '../../components/shared/PageHeadingBanner';
import { customBaseUrl, httpGet } from '../../store/http';
import { showLoader, hideLoader } from '../../utils/loader';
import Swal from 'sweetalert2';
import FullResultModal from '../../components/shared/FullResultModal';
import { httpGetBlob } from '../../store/http';
import './CustomerManagement.css';

const searchTypes = [
  { value: 'userId', label: 'User ID', placeholder: 'Enter User ID...' },
  { value: 'accountNumber', label: 'Account Number', placeholder: 'Enter Account Number...' },
];

const CustomersList = ({ userType = 'PERSONAL' }) => {
  const navigate = useNavigate();
  // We utilize empty arrays natively reflecting standard API start-states. NO dummy data!
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchText, setSearchText] = useState('');
  const [searchType, setSearchType] = useState('userId');
  const [hasSearched, setHasSearched] = useState(false);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [businessType, setBusinessType] = useState(null);

  const fetchCustomers = async (page = 0, size = 10, isSearch = false, userType = 'PERSONAL') => {
    setLoading(true);
    showLoader();
    try {
      let url = `${customBaseUrl.auditUrl}/admin/customers?page=${page}&pageSize=${size}&userType=${userType}&sortDirection=DESC`;

      if (userType === 'CORPORATE' && businessType) {
        url += `&businessType=${businessType}`;
      }

      if (isSearch && searchText) {
        if (searchType === 'userId') {
          url = `${customBaseUrl.auditUrl}/admin/customers/${searchText}`;
        } else if (searchType === 'accountNumber') {
          url = `${customBaseUrl.auditUrl}/admin/customers/${searchText}/account`;
        }
      }

      const response = await httpGet(url);

      let dataArr = [];
      let totalCount = 0;

      // The API might return the object directly for ID searches
      const rawData = response?.data || response;

      if (isSearch && searchText) {
        if (rawData && !Array.isArray(rawData)) {
          dataArr = [rawData];
          totalCount = 1;
        } else if (Array.isArray(rawData)) {
          dataArr = rawData;
          totalCount = response?.total || dataArr.length;
        }
      } else {
        dataArr = response?.data || [];
        totalCount = response?.total || dataArr.length;
      }

      setCustomers(dataArr);
      setFilteredCustomers(dataArr);
      setPagination({
        ...pagination,
        current: (response?.pageNo !== undefined ? response.pageNo : page) + 1,
        pageSize: size,
        total: totalCount
      });
      setHasSearched(true);
    } catch (e) {
      console.error('Error fetching customers:', e);
      Swal.fire('Error', 'Unable to fetch record at this time.', 'error');
    } finally {
      hideLoader();
      setLoading(false);
    }
  };

  const handleExport = async (userType = 'PERSONAL') => {
    showLoader();
    try {
      const url = `${customBaseUrl.auditUrl}/admin/customers/export?userType=${userType}`;
      const blob = await httpGetBlob(url);
      if (blob) {
        const downloadUrl = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', `customers-export-${new Date().getTime()}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (e) {
      console.error('Export error:', e);
      Swal.fire('Export Failed', 'Unable to download export stream.', 'error');
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    // We removed the automatic on-mount fetch to enforce search-first logic
    // fetchCustomers();
  }, []);

  const handleTableChange = (pag) => {
    fetchCustomers(pag.current - 1, pag.pageSize, !!searchText, userType);
  };

  const handleSearchTrigger = () => {
    if (!searchText) {
      fetchCustomers(0, pagination.pageSize, false, userType);
    } else {
      fetchCustomers(0, pagination.pageSize, true, userType);
    }
  };

  const currentTypeMeta = searchTypes.find(t => t.value === searchType);

  const getActionMenuItems = (record) => {
    const items = [
      {
        key: 'view',
        label: 'View Details',
        icon: <UserOutlined />,
        onClick: () => navigate(`/customer-reports/${record.id}`),
      },
      { type: 'divider' },
    ];

    if (record.isLocked) {
      items.push({
        key: 'unblock',
        label: 'Unblock Account',
        icon: <CheckCircleOutlined />,
        onClick: () => console.log('Unblock', record.id),
      });
    } else {
      items.push({
        key: 'block',
        label: 'Block Account',
        icon: <StopOutlined style={{ color: '#ff4d4f' }} />,
        onClick: () => console.log('Block', record.id),
        danger: true,
      });
    }

    if (record.isActive) {
      items.push({
        key: 'deactivate',
        label: 'Deactivate',
        icon: <StopOutlined />,
        onClick: () => console.log('Deactivate', record.id),
      });
    } else {
      items.push({
        key: 'activate',
        label: 'Activate',
        icon: <CheckCircleOutlined />,
        onClick: () => console.log('Activate', record.id),
      });
    }
    return items;
  };

  const columns = [
    {
      title: 'Customer Name',
      dataIndex: 'fullName',
      key: 'fullName',
      render: (name) => <span style={{ fontWeight: 600, color: '#1a1a1a', textTransform: 'capitalize' }}>{name}</span>,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Phone Number',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
    },
    {
      title: 'Customer Type',
      dataIndex: 'userType',
      key: 'userType',
      render: (type) => (
        <Tag style={{
          fontFamily: 'monospace',
          background: '#e0f7fa',
          color: '#006064',
          border: '1px solid #b2ebf2',
          borderRadius: 6,
          fontWeight: 600,
          textTransform: 'uppercase'
        }}>
          {type}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Tag style={{
          color: isActive ? '#52c41a' : '#ff4d4f',
          background: isActive ? '#f6ffed' : '#fff2f0',
          border: `1px solid ${isActive ? '#b7eb8f' : '#ffa39e'}`,
          borderRadius: 20,
          fontWeight: 600,
          fontSize: 11,
          padding: '2px 10px',
        }}>
          {isActive ? 'ACTIVE' : 'INACTIVE'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Dropdown menu={{ items: getActionMenuItems(record) }} trigger={['click']} placement="bottomRight">
          <Button type="text" icon={<EllipsisOutlined style={{ fontSize: 20, fontWeight: 700 }} />} className="customer-action-btn" />
        </Dropdown>
      ),
    },
  ];

  const activeCount = filteredCustomers.filter(c => c.isActive).length;
  const inactiveCount = filteredCustomers.filter(c => !c.isActive).length;

  return (
    <div className="customer-list-wrapper">
      <PageHeadingBanner
        icon={<UserOutlined />}
        title="Customer Reports"
        subtitle="Analyze and view historical customer data reports"
        actions={null}
      />



      <div className="customer-search-bar" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        {userType === 'CORPORATE' && (
          <Select
            placeholder="Business Type"
            style={{ width: 200, height: '42px' }}
            allowClear
            onChange={(val) => {
              setBusinessType(val);
              // Trigger auto-fetch on filter change for better UX
              fetchCustomers(0, pagination.pageSize, false, userType);
            }}
            value={businessType}
            className="customer-search-select"
            size="large"
          >
            <Select.Option value="CAC_BUSINESS">Registered (CAC)</Select.Option>
            {/* <Select.Option value="NON_CAC_BUSINESS">Non-Registered (Non-CAC)</Select.Option> */}
          </Select>
        )}
        <Select
          value={searchType}
          onChange={(value) => setSearchType(value)}
          style={{ width: 180, height: '42px' }}
          className="customer-search-select"
          size="large"
        >
          {searchTypes.map(t => <Select.Option key={t.value} value={t.value}>{t.label}</Select.Option>)}
        </Select>
        <Input
          prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
          placeholder={currentTypeMeta?.placeholder || "Search..."}
          allowClear
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onPressEnter={handleSearchTrigger}
          className="customer-search-input"
          style={{ flex: 1 }}
        />
        <Button
          type="primary"
          onClick={handleSearchTrigger}
          style={{ height: '42px', background: '#ff6700', borderColor: '#ff6700', borderRadius: '8px' }}
        >
          Search
        </Button>
        <Button
          icon={<DownloadOutlined />}
          size="large"
          onClick={() => handleExport(userType)}
          style={{ borderRadius: '8px', color: '#ff6700', borderColor: '#ff6700', height: '42px' }}
          title="Export Data"
        />
      </div>

      <div className="customer-table-card">
        {hasSearched ? (
          <Table
            columns={columns}
            dataSource={filteredCustomers}
            rowKey="id"
            loading={loading}
            pagination={{ ...pagination, showSizeChanger: true }}
            onChange={handleTableChange}
            onRow={(record) => ({
              onClick: () => {
                setSelectedRecord(record);
                setIsModalOpen(true);
              },
              style: { cursor: 'pointer' }
            })}
            rowClassName={(_, idx) => (idx % 2 === 0 ? 'customer-row-even' : 'customer-row-odd')}
            className="customer-table"
          />
        ) : (
          <div style={{ padding: '80px 0', textAlign: 'center', background: '#fff', borderRadius: '12px' }}>
            <SearchOutlined style={{ fontSize: 48, color: '#f0f0f0', marginBottom: 16 }} />
            <h3 style={{ color: '#8c8c8c' }}>Generate a Customer Report</h3>
            <p style={{ color: '#bfbfbf' }}>Use the search filters above to pull specific data records</p>
          </div>
        )}
      </div>

      <FullResultModal
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        data={selectedRecord}
        title="Customer Record Details"
      />


    </div>
  );
};

export default CustomersList;
